import { useCallback, useEffect, useRef, useState } from "react";

import {
  getSessionEntriesQueryControllerGet,
  listTodaySessionsQueryControllerList,
} from "@/generated/log-types/log-types";
import type {
  LogEntryResponseDto,
  TodaySessionResponseDto,
} from "@/generated/model";
import type { EmergencyLightCheckOutcome } from "@/hooks/use-offline-emergency-light-check";
import {
  generateClientId,
  offlineDb,
  type OfflineLogEntry,
  requireCachedUserId,
} from "@/lib/offline/db";
import { syncService, type SyncStatus } from "@/lib/offline/syncService";

/** Extended entry type that includes local sync state */
export interface LogEntryWithSync extends LogEntryResponseDto {
  localSyncState?: "pending" | "synced" | "error";
}

export interface CreateLogEntryParams {
  logTypeId: string;
  data: Record<string, unknown>;
  roomId?: string;
  notes?: string;
  /** Set for fridge-temperature submissions so the server can snapshot
   *  thresholds + open an incident at sync time (NAU-219d, AC#15). */
  coldStorageId?: string;
  /** Cached display name so the panel can render the pending entry
   *  without a network fetch. */
  coldStorageName?: string;
  /** Set for the weekly fire alarm test so the record names the call point
   *  that was tested (NAU-411 AC23). */
  fireAlarmCallPointId?: string;
  /** Set for a fire door inspection so the record names the door (NAU-441). */
  fireDoorId?: string;
}

export interface CreateLogEntryOptions {
  /**
   * Wait for the server's answer when online, so the caller can report a
   * rejection instead of a success the queue will never deliver.
   */
  awaitSync?: boolean;
}

export interface CreateLogEntryResult {
  /** Without `awaitSync` this is always `queued`: nothing waited for the server. */
  outcome: EmergencyLightCheckOutcome;
  /** Only set on `failed` — the server's reason. */
  error?: string;
  /** Only set on `synced` — the id the server gave the entry. */
  serverId?: string;
}

export interface UseOfflineLogEntriesResult {
  entries: LogEntryWithSync[];
  sessions: TodaySessionResponseDto[];
  isLoading: boolean;
  isOffline: boolean;
  syncStatus: { pendingCount: number; failedCount: number };
  createEntry: (
    params: CreateLogEntryParams,
    options?: CreateLogEntryOptions,
  ) => Promise<CreateLogEntryResult>;
  refetch: () => Promise<void>;
}

/**
 * Entries whose `createEntry` call is still waiting on the sync. Module-level
 * because the cleanup that deletes synced rows can run from any mounted hook.
 */
const entriesAwaitingServerId = new Set<string>();

/** Deletes the nursery's synced rows the server list now has, except any still being awaited. */
export async function deleteReconciledEntries(
  nurseryId: string,
  serverIds: Set<string>,
): Promise<void> {
  const syncedLocal = await offlineDb.logEntries
    .filter(
      (e) =>
        e.nurseryId === nurseryId &&
        e.syncState === "synced" &&
        !!e.serverId &&
        serverIds.has(e.serverId) &&
        !entriesAwaitingServerId.has(e.id),
    )
    .toArray();
  for (const entry of syncedLocal) {
    await offlineDb.logEntries.delete(entry.id);
  }
}

/** Syncs now and reads the entry back, holding it from the cleanup until it has been read. */
export async function syncAndRead(
  entryId: string,
): Promise<OfflineLogEntry | undefined> {
  entriesAwaitingServerId.add(entryId);
  try {
    await syncService.immediateSync();
    return await offlineDb.logEntries.get(entryId);
  } finally {
    entriesAwaitingServerId.delete(entryId);
  }
}

/**
 * Offline-first hook for log entries. Generic — works for any log type.
 *
 * Flow:
 * 1. Immediately return cached data from IndexedDB
 * 2. If online, fetch fresh data from server in background
 * 3. Merge server entries with pending local entries
 * 4. createEntry() writes to IndexedDB + enqueues sync
 */
export function useOfflineLogEntries(
  nurseryId: string | null,
): UseOfflineLogEntriesResult {
  const [serverEntries, setServerEntries] = useState<LogEntryResponseDto[]>([]);
  const [localEntries, setLocalEntries] = useState<OfflineLogEntry[]>([]);
  const [sessions, setSessions] = useState<TodaySessionResponseDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [syncStatus, setSyncStatus] = useState({
    pendingCount: 0,
    failedCount: 0,
  });

  const nurseryIdRef = useRef(nurseryId);
  nurseryIdRef.current = nurseryId;

  // Load cached sessions + entries from IndexedDB
  const loadFromCache = useCallback(async () => {
    if (!nurseryId) return;

    const cachedSessions = await offlineDb.logSessionsCache.get(
      `log-sessions-${nurseryId}`,
    );
    if (cachedSessions) {
      setSessions(cachedSessions.sessions as TodaySessionResponseDto[]);
    }

    // Load pending local entries for this nursery
    const pending = await offlineDb.logEntries
      .filter((e) => e.nurseryId === nurseryId && e.syncState !== "synced")
      .toArray();
    setLocalEntries(pending);

    // Load cached server entries for any active sessions
    if (cachedSessions) {
      const sessionList = cachedSessions.sessions as TodaySessionResponseDto[];
      const allEntries: LogEntryResponseDto[] = [];
      for (const session of sessionList) {
        if (session.sessionId) {
          const cached = await offlineDb.logEntriesCache.get(
            `log-entries-${session.sessionId}`,
          );
          if (cached) {
            allEntries.push(...(cached.entries as LogEntryResponseDto[]));
          }
        }
      }
      setServerEntries(allEntries);
    }
  }, [nurseryId]);

  // Fetch from server and update cache
  const fetchFromServer = useCallback(async () => {
    if (!nurseryId || !navigator.onLine) return;

    try {
      // Fetch today's sessions
      const sessionsRes = await listTodaySessionsQueryControllerList(nurseryId);
      const sessionList = (sessionsRes.data ??
        sessionsRes) as unknown as TodaySessionResponseDto[];
      setSessions(sessionList);

      // Cache sessions
      await offlineDb.logSessionsCache.put({
        id: `log-sessions-${nurseryId}`,
        nurseryId,
        sessions: sessionList,
        cachedAt: Date.now(),
      });

      // Fetch entries for each session that has one
      const allEntries: LogEntryResponseDto[] = [];
      for (const session of sessionList) {
        if (session.sessionId) {
          const entriesRes = await getSessionEntriesQueryControllerGet(
            session.sessionId,
          );
          const entries = (entriesRes.data ??
            entriesRes) as unknown as LogEntryResponseDto[];
          allEntries.push(...entries);

          // Cache entries
          await offlineDb.logEntriesCache.put({
            id: `log-entries-${session.sessionId}`,
            sessionId: session.sessionId,
            entries,
            cachedAt: Date.now(),
          });
        }
      }
      setServerEntries(allEntries);

      // Clean up synced local entries that now exist on server
      const serverIds = new Set(allEntries.map((e) => e.id));
      await deleteReconciledEntries(nurseryId, serverIds);

      // Refresh local entries
      const pending = await offlineDb.logEntries
        .filter((e) => e.nurseryId === nurseryId && e.syncState !== "synced")
        .toArray();
      setLocalEntries(pending);
    } catch (err) {
      console.error("[OfflineLogEntries] Failed to fetch from server:", err);
      setIsOffline(true);
    }
  }, [nurseryId]);

  // Initial load
  useEffect(() => {
    if (!nurseryId) {
      setIsLoading(false);
      return;
    }

    (async () => {
      setIsLoading(true);
      await loadFromCache();
      if (navigator.onLine) {
        await fetchFromServer();
      }
      setIsLoading(false);
    })();
  }, [nurseryId, loadFromCache, fetchFromServer]);

  // Online/offline listeners
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      fetchFromServer();
    };
    const handleOffline = () => setIsOffline(true);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [fetchFromServer]);

  // Subscribe to sync service — refetch when syncing finishes
  useEffect(() => {
    let wasSyncing = false;
    const unsubscribe = syncService.subscribe((status: SyncStatus) => {
      // Refetch when sync transitions from syncing → not syncing
      if (wasSyncing && !status.isSyncing && navigator.onLine) {
        fetchFromServer();
      }
      wasSyncing = status.isSyncing;
    });
    return () => {
      unsubscribe();
    };
  }, [fetchFromServer]);

  // Track sync status for local log entries
  useEffect(() => {
    (async () => {
      const queueItems = await offlineDb.syncQueue
        .filter((i) => i.type === "logEntry")
        .toArray();
      const pendingCount = queueItems.filter((i) => i.attempts < 5).length;
      const failedCount = queueItems.filter((i) => i.attempts >= 5).length;
      setSyncStatus({ pendingCount, failedCount });
    })();
  }, [localEntries]);

  // Create a log entry (works offline)
  const createEntry = useCallback(
    async (
      params: CreateLogEntryParams,
      options: CreateLogEntryOptions = {},
    ): Promise<CreateLogEntryResult> => {
      if (!nurseryIdRef.current) {
        return { outcome: "failed", error: "No nursery selected" };
      }

      // Get current user info for display
      const userSession = await offlineDb.userSession.get("current-user");
      const userName = userSession
        ? [userSession.firstName, userSession.lastName]
            .filter(Boolean)
            .join(" ") ||
          userSession.email ||
          "You"
        : "You";

      const entry: OfflineLogEntry = {
        id: generateClientId(),
        nurseryId: nurseryIdRef.current,
        logTypeId: params.logTypeId,
        roomId: params.roomId,
        coldStorageId: params.coldStorageId,
        coldStorageName: params.coldStorageName,
        fireAlarmCallPointId: params.fireAlarmCallPointId,
        fireDoorId: params.fireDoorId,
        notes: params.notes,
        data: params.data,
        timestamp: new Date().toISOString(),
        // No empty-string fallback: the sync layer drops a falsy author, which
        // would silently replay this log entry under whoever drains the queue —
        // exactly the mis-attribution NAU-399 exists to prevent. A missing
        // cached session means we cannot say who is recording, so refuse
        // rather than capture something we know will be attributed wrongly.
        recordedByUserId: requireCachedUserId(userSession?.userId),
        recordedByName: userName,
        syncState: "pending",
      };

      // Write to IndexedDB
      await offlineDb.logEntries.add(entry);

      // Enqueue for sync
      await offlineDb.syncQueue.add({
        id: generateClientId(),
        type: "logEntry",
        entityId: entry.id,
        priority: 3,
        createdAt: Date.now(),
        attempts: 0,
      });

      // Optimistically update local state
      setLocalEntries((prev) => [entry, ...prev]);

      if (!navigator.onLine) return { outcome: "queued" };

      if (!options.awaitSync) {
        syncService.debouncedSync();
        return { outcome: "queued" };
      }

      // The queue swallows a failed replay, so immediateSync resolving proves
      // nothing; the stored row is the only signal the server took it.
      const stored = await syncAndRead(entry.id);
      // Gone means synced and already reconciled against the server list.
      if (!stored) return { outcome: "synced" };
      if (stored.syncState === "synced") {
        return { outcome: "synced", serverId: stored.serverId };
      }
      // No server reason means no answer (e.g. weak signal); the queue retries it.
      if (!stored.lastError) return { outcome: "queued" };

      // Rejected: drop the queued copy, or the queue's retry plus the user's
      // corrected resubmission would record the test twice.
      await offlineDb.syncQueue
        .filter((i) => i.type === "logEntry" && i.entityId === entry.id)
        .delete();
      await offlineDb.logEntries.delete(entry.id);
      setLocalEntries((prev) => prev.filter((e) => e.id !== entry.id));
      return { outcome: "failed", error: stored.lastError };
    },
    [],
  );

  const refetch = useCallback(async () => {
    await fetchFromServer();
  }, [fetchFromServer]);

  // Merge server + local entries, deduplicating synced locals
  const mergedEntries: LogEntryWithSync[] = (() => {
    const serverIds = new Set(serverEntries.map((e) => e.id));

    // Convert local pending entries to LogEntryWithSync shape
    const localAsEntries: LogEntryWithSync[] = localEntries
      .filter((le) => !le.serverId || !serverIds.has(le.serverId))
      .map((le) => ({
        id: le.id,
        sessionId: "",
        logTypeId: le.logTypeId,
        timestamp: le.timestamp,
        createdAt: le.timestamp,
        data: le.data,
        notes: le.notes ?? null,
        recordedBy: {
          id: le.recordedByUserId,
          firstName: le.recordedByName.split(" ")[0] ?? null,
          lastName: le.recordedByName.split(" ").slice(1).join(" ") || null,
          email: "",
        },
        // Nobody has submitted this yet — it is still sitting in the queue.
        // The server fills this in at sync time if a different account ends up
        // sending it (NAU-399).
        submittedBy: null,
        room: le.roomId ? { id: le.roomId, name: "" } : null,
        fireAlarmCallPoint: le.fireAlarmCallPointId
          ? { id: le.fireAlarmCallPointId, name: "" }
          : null,
        fireDoor: le.fireDoorId
          ? { id: le.fireDoorId, name: "", room: null }
          : null,
        // NAU-219d: surface the cached fridge so FridgeTempPanel's
        // `e.coldStorage?.id === selectedUnitId` filter picks the local
        // pending row up alongside server entries.
        coldStorage: le.coldStorageId
          ? { id: le.coldStorageId, name: le.coldStorageName ?? "" }
          : null,
        thresholdMinAtLog: null,
        thresholdMaxAtLog: null,
        localSyncState: le.syncState,
      }));

    // Combine and sort by timestamp desc (most recent first)
    return [...localAsEntries, ...serverEntries].sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
  })();

  return {
    entries: mergedEntries,
    sessions,
    isLoading,
    isOffline,
    syncStatus,
    createEntry,
    refetch,
  };
}
